import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldAlert, Activity, Timer, CheckCircle2, TrendingDown, AlertTriangle } from "lucide-react";
import { getDashboard } from "@/lib/api";
import { fmtPct, fmtRelative } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";
import StatusBadge from "@/components/StatusBadge";
import ConfidenceMeter from "@/components/ConfidenceMeter";
import ContributorClassTag from "@/components/ContributorClassTag";
import PolicyFlag from "@/components/PolicyFlag";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar,
} from "recharts";

const kpiToneClass = (k) => {
  if (k.warn) return "text-rose-700";
  if (k.good) return "text-emerald-700";
  return "text-muted-foreground";
};

export default function Overview() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  useEffect(() => {
    getDashboard()
      .then(setData)
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Failed to load dashboard");
        setLoadError(true);
      });
  }, []);
  if (loadError) return <div className="p-10 text-rose-600">Failed to load dashboard. Please refresh.</div>;
  if (!data) return (
    <div className="p-8 md:p-10 space-y-8">
      {/* KPI skeleton */}
      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-border bg-card p-5 space-y-3">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-2.5 w-20" />
          </div>
        ))}
      </section>
      {/* Chart skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-lg border border-border bg-card p-6 space-y-4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-48 w-full" />
        </div>
        <div className="rounded-lg border border-border bg-card p-6 space-y-4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    </div>
  );
  const { kpis, queue_by_stage, trend, sla_by_domain, priority_artifacts, policy_alerts, activity } = data;

  const kpiCards = [
    { label: "Candidates this week", value: kpis.candidates_this_week, delta: "+6 vs last week", icon: Activity },
    { label: "Avg. time to approval", value: `${kpis.avg_time_to_approval_hours}h`, delta: "-18% · faster", icon: Timer, good: true },
    { label: "Approval rate", value: fmtPct(kpis.approval_rate), delta: "+4pp vs 30d", icon: CheckCircle2 },
    { label: "Disputed items", value: kpis.disputed_count, delta: "2 need attention", icon: AlertTriangle, warn: true },
    { label: "Revocations this month", value: kpis.revocations_this_month, delta: "1 high-risk", icon: TrendingDown },
    { label: "Evidence completeness", value: fmtPct(kpis.evidence_completeness_pct), delta: "target ≥ 95%", icon: ShieldAlert },
  ];

  return (
    <div className="p-8 md:p-10 space-y-8" data-testid="overview-page">
      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpiCards.map((k) => (
          <div key={k.label} className="rounded-lg border border-border bg-card p-5 hover:bg-muted/30 transition-colors">
            <div className="flex items-center justify-between">
              <span className="tracked-caps">{k.label}</span>
              <k.icon className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <div className="mt-4 font-serif-display text-3xl leading-none">{k.value}</div>
            <div className={`mt-2 font-mono text-[11px] ${kpiToneClass(k)}`}>{k.delta}</div>
          </div>
        ))}
      </section>

      <section className="grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-8 rounded-lg border border-border bg-card p-6">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="tracked-caps">Time from pattern to approved artifact</div>
              <h3 className="mt-1 font-serif-display text-xl">Trending down, 7-week window</h3>
            </div>
            <span className="font-mono text-xs text-muted-foreground">days</span>
          </div>
          <div className="mt-6 h-60" style={{ minHeight: 200 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
              <LineChart data={trend} margin={{ left: -15, right: 10, top: 10, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="week" tickLine={false} axisLine={false} fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis tickLine={false} axisLine={false} fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Line type="monotone" dataKey="days_to_approval" stroke="hsl(var(--brand))" strokeWidth={2} dot={{ r: 3, fill: "hsl(var(--brand))" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-4 rounded-lg border border-border bg-card p-6">
          <div className="tracked-caps">Queue by stage</div>
          <div className="mt-4 h-60" style={{ minHeight: 200 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
              <BarChart data={queue_by_stage} margin={{ left: -15, right: 10, top: 10, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="stage" tickLine={false} axisLine={false} fontSize={10} stroke="hsl(var(--muted-foreground))" />
                <YAxis tickLine={false} axisLine={false} fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="count" fill="hsl(var(--brand))" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-8 rounded-lg border border-border bg-card overflow-hidden">
          <div className="flex items-baseline justify-between p-6 pb-4">
            <div>
              <div className="tracked-caps">Priority queue</div>
              <h3 className="mt-1 font-serif-display text-xl">Highest-priority candidate artifacts</h3>
            </div>
            <Link to="/app/artifacts" className="text-sm text-brand inline-flex items-center gap-1 hover:underline" data-testid="view-all-artifacts">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="tracked-caps text-left">
                <th className="px-6 py-2 font-medium">ID</th>
                <th className="px-6 py-2 font-medium">Title</th>
                <th className="px-6 py-2 font-medium">Class</th>
                <th className="px-6 py-2 font-medium">Confidence</th>
                <th className="px-6 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {priority_artifacts.map((a) => (
                <tr key={a.id} className="border-t border-border hover:bg-muted/40 transition-colors">
                  <td className="px-6 py-3 font-mono text-xs text-muted-foreground">{a.id}</td>
                  <td className="px-6 py-3">
                    <Link to={`/app/artifacts/${a.id}`} className="hover:underline" data-testid={`priority-row-${a.id}`}>{a.title}</Link>
                    <div className="text-xs text-muted-foreground">{a.domain}</div>
                  </td>
                  <td className="px-6 py-3"><ContributorClassTag value={a.contributor_classes[0]} count={a.contributor_count} /></td>
                  <td className="px-6 py-3"><ConfidenceMeter value={a.confidence} /></td>
                  <td className="px-6 py-3"><StatusBadge value={a.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="col-span-12 lg:col-span-4 space-y-5">
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="tracked-caps">Policy alerts</div>
            <ul className="mt-4 space-y-3">
              {policy_alerts.map((a) => (
                <li key={a.id} className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm">{a.flag}</div>
                    <Link to={`/app/artifacts/${a.artifact_id}`} className="font-mono text-[11px] text-muted-foreground hover:underline">{a.artifact_id}</Link>
                  </div>
                  <PolicyFlag label={a.severity} severity={a.severity} />
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="tracked-caps">Review SLA · by domain</div>
            <ul className="mt-4 space-y-3">
              {sla_by_domain.map((d) => {
                const breach = d.actual_hours > d.target_hours;
                return (
                  <li key={d.domain} className="text-sm">
                    <div className="flex items-center justify-between">
                      <span>{d.domain}</span>
                      <span className={`font-mono text-[11px] ${breach ? "text-rose-700" : "text-emerald-700"}`}>
                        {d.actual_hours}h · target {d.target_hours}h
                      </span>
                    </div>
                    <div className="mt-1.5 h-1 rounded bg-muted overflow-hidden">
                      <div className={`h-full ${breach ? "bg-rose-500" : "bg-emerald-600"}`} style={{ width: `${Math.min(100, (d.actual_hours / d.target_hours) * 100)}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-border bg-card p-6">
        <div className="tracked-caps">Recent activity</div>
        <ul className="mt-4 divide-y divide-border/80">
          {activity.map((a) => (
            <li key={`${a.ts}-${a.actor}-${a.kind}`} className="py-3 flex items-baseline justify-between gap-4">
              <div className="flex items-baseline gap-3">
                <span className="font-mono text-[11px] text-muted-foreground w-20">{fmtRelative(a.ts)}</span>
                <span className="text-sm">{a.actor}</span>
                <span className="text-sm text-foreground/75">{a.action}</span>
              </div>
              <span className="tracked-caps">{a.kind}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-lg border border-brand/30 bg-brand/5 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="font-serif-display text-xl">Bring this dashboard to your team.</h3>
          <p className="mt-1 text-sm text-foreground/70 max-w-md">
            This is a live demo. ProofMark gives your ops and compliance team a real governance record — candidate extraction, human review, evidence chain, and revocation.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-md bg-brand text-brand-foreground px-4 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Request access
          </Link>
          <a href="/#how" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            How it works →
          </a>
        </div>
      </section>
    </div>
  );
}
